import { useEffect, useState } from 'react';
import { getSeguimientosPendientes, createSeguimiento, getClientes, getProspectos } from '../services/api';
import Modal from '../components/ui/Modal';

const TIPO_ICONS: Record<string, string> = {
  Llamada:  '📞',
  WhatsApp: '💬',
  Email:    '📧',
  Visita:   '🤝',
};

const RESULTADO_COLORS: Record<string, string> = {
  Interesado:      'bg-green-100 text-green-700',
  'No interesado': 'bg-red-100 text-red-700',
  Callback:        'bg-yellow-100 text-yellow-700',
  'Sin respuesta': 'bg-gray-100 text-gray-500',
};

const EMPTY_FORM = {
  tipo: 'Llamada', resultado: 'Callback', notas: '',
  proximoContacto: '', clienteId: '', prospectoId: '',
};

export default function Seguimientos() {
  const [seguimientos, setSeguimientos] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [prospectos, setProspectos] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => getSeguimientosPendientes().then(res => setSeguimientos(res.data));

  useEffect(() => {
    load();
    getClientes().then(res => setClientes(res.data));
    getProspectos().then(res => setProspectos(res.data));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createSeguimiento({
        tipo: form.tipo,
        resultado: form.resultado,
        notas: form.notas || null,
        proximoContacto: form.proximoContacto || null,
        clienteId: form.clienteId ? Number(form.clienteId) : null,
        prospectoId: form.prospectoId ? Number(form.prospectoId) : null,
      });
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch {
      setError('Error al guardar. Verifica los datos.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof typeof EMPTY_FORM) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });

  const isOverdue = (fecha: string) => new Date(fecha) < new Date();

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Seguimientos</h2>
          <p className="text-sm text-gray-500 mt-0.5">Próximos 7 días y vencidos</p>
        </div>
        <button
          onClick={() => { setShowModal(true); setForm(EMPTY_FORM); setError(''); }}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700"
        >
          + Registrar seguimiento
        </button>
      </div>

      <div className="space-y-3">
        {seguimientos.map(s => (
          <div
            key={s.id}
            className={`bg-white rounded-xl shadow-sm p-4 flex items-start gap-4 border-l-4 ${
              s.proximoContacto && isOverdue(s.proximoContacto)
                ? 'border-red-400'
                : 'border-primary-400'
            }`}
          >
            <div className="text-2xl">{TIPO_ICONS[s.tipo] || '📋'}</div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-800">
                    {s.cliente?.nombre || s.prospecto?.nombre || 'Sin asignar'}
                  </p>
                  <p className="text-xs text-gray-400">{s.tipo} · {s.agente?.nombre}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${RESULTADO_COLORS[s.resultado] || 'bg-gray-100 text-gray-600'}`}>
                  {s.resultado}
                </span>
              </div>
              {s.notas && <p className="text-sm text-gray-600 mt-1">{s.notas}</p>}
              {s.proximoContacto && (
                <p className={`text-xs mt-2 font-medium ${isOverdue(s.proximoContacto) ? 'text-red-500' : 'text-primary-600'}`}>
                  {isOverdue(s.proximoContacto) ? '⚠️ Vencido · ' : '📅 Próximo contacto: '}
                  {new Date(s.proximoContacto).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                </p>
              )}
            </div>
          </div>
        ))}

        {seguimientos.length === 0 && (
          <div className="bg-white rounded-xl p-8 text-center text-gray-400">
            No hay seguimientos pendientes
          </div>
        )}
      </div>

      {showModal && (
        <Modal title="Registrar seguimiento" onClose={() => setShowModal(false)} size="md">
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
              <select {...field('tipo')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {Object.keys(TIPO_ICONS).map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resultado *</label>
              <select {...field('resultado')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {Object.keys(RESULTADO_COLORS).map(r => <option key={r}>{r}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
              <select {...field('clienteId')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">— Ninguno —</option>
                {clientes.map((c: any) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prospecto</label>
              <select {...field('prospectoId')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">— Ninguno —</option>
                {prospectos.map((p: any) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Próximo contacto</label>
              <input type="datetime-local" {...field('proximoContacto')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea {...field('notas')} rows={3}
                placeholder="Resultado de la llamada, acuerdos, observaciones..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>

            {error && <p className="col-span-2 text-red-500 text-sm">{error}</p>}

            <div className="col-span-2 flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Guardando...' : 'Registrar'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
